import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { useThemeStore } from '@/stores/theme'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import { i18n } from './i18n'
import { resolveLocale } from './i18n/locale'
import { initSentry } from './api/sentry'
import { initPostHog, track } from './api/posthog'
import { isUpgradeRequired } from '@/composables/useMinVersion'
import { setupAppLifecycle } from '@/composables/useAppLifecycle'
import { setupDeepLinks } from '@/composables/useDeepLinks'
import { setupStatusBar } from '@/composables/useStatusBar'
import { findOrphanRecordings } from '@/composables/useOrphanRecording'
import { detectProblematicOem } from '@/composables/useOemWhitelist'
import { setupOta } from '@/composables/useOtaUpdate'
import { initTeams } from '@/composables/useTeams'
import { storage } from '@/api/preferences'
import { Capacitor } from '@capacitor/core'
import './style.css'

const app = createApp(App)
initSentry(app)
initPostHog()
track('app_opened', { source: 'icon' })
const pinia = createPinia()
app.use(pinia)
app.use(i18n)

app.config.errorHandler = (err, _instance, info) => {
  console.error('[vue] uncaught', err, info, err?.stack)
  if (typeof window !== 'undefined' && window.Sentry?.captureException) {
    window.Sentry.captureException(err)
  }
  try {
    const { t } = i18n.global
    useToastStore().error(t('errors.global_handler'))
  } catch {
    // pinia not ready yet (very early boot) — log only
  }
}

// Hydrate the auth store BEFORE installing the router. app.use(router)
// triggers Vue Router's initial navigation synchronously, so the route guards
// run right here — if the session isn't restored yet, an authenticated user
// is routed to /login on every cold start and stays stranded there until the
// next navigation re-evaluates the (by-then hydrated) guards.
try {
  await useAuthStore(pinia).hydrate()
} catch (err) {
  console.error('[boot] auth hydration failed, continuing unauthenticated', err)
}

app.use(router)

useThemeStore(pinia).init()

// Si la SPA corre embebida en Microsoft Teams, inicializa su SDK (apaga el
// spinner de carga y sigue el tema de Teams). Se espera antes del mount para
// que isInTeams() sea correcto cuando Login.vue monta y dispara el SSO.
// Fuera de Teams, app.initialize() rechaza instantáneamente → no bloquea.
await initTeams().catch((e) => console.warn('[boot] Teams init skipped', e))

// Cold start with a cached session: the router guard only calls fetchMe when
// the user is missing, so refresh /me here in the background. Fire-and-forget
// — it must not block mount, and on a transient failure fetchMe keeps the
// session intact rather than logging the user out.
{
  const auth = useAuthStore(pinia)
  if (auth.token && auth.user) {
    auth.fetchMe().catch(() => {})
  }
}

// Set the initial locale based on user → cookie → device → navigator → default.
{
  const authStoreForLocale = useAuthStore(pinia)
  const resolvedLocale = await resolveLocale(authStoreForLocale.user?.locale)
  i18n.global.locale.value = resolvedLocale
  document.documentElement.lang = resolvedLocale
}

// First-launch welcome: on native, unauthenticated users see the welcome
// screen once before the login screen. Flag is persisted in storage so it
// only fires for true first launches.
if (Capacitor.isNativePlatform()) {
  try {
    const auth = useAuthStore(pinia)
    if (!auth.isAuthenticated) {
      const seen = await storage.get('pavemi_welcome_seen')
      if (!seen) {
        await storage.set('pavemi_welcome_seen', '1')
        router.push('/welcome')
      }
    }
  } catch (e) {
    console.warn('[boot] welcome check failed, continuing', e)
  }
}

if (await isUpgradeRequired()) {
  router.push('/force-upgrade')
}

// Check for recordings that were interrupted by an app crash or OS kill.
// Runs only on native (iOS/Android); on web Filesystem ops are no-ops.
if (Capacitor.isNativePlatform()) {
  try {
    const orphans = await findOrphanRecordings()
    if (orphans.length > 0) {
      router.push('/recovery')
    }
  } catch (err) {
    // Non-fatal: if Filesystem throws for any reason, continue normal boot.
    console.warn('[boot] orphan recording check failed, continuing', err)
  }
}

// Detect recordings killed by OEM battery optimizers: if the in-progress marker
// was left behind and the heartbeat is >30s stale, the process was killed externally.
if (Capacitor.isNativePlatform()) {
  try {
    const raw = await storage.get('pavemi_recording_in_progress')
    if (raw) {
      const rec = JSON.parse(raw)
      const sinceLast = Date.now() - (rec.lastHeartbeat ?? rec.startedAt ?? 0)
      if (sinceLast > 30_000) {
        const oem = await detectProblematicOem()
        // Stash a flag for Home to consume on first mount
        await storage.set('pavemi_recording_killed_flag', JSON.stringify({
          at: Date.now(),
          oem: oem ? { manufacturer: oem.manufacturer, label: oem.label } : null,
          lostMs: sinceLast,
        }))
        // Best-effort tracking (posthog wrapper may or may not be initialized yet)
        try {
          const { track } = await import('@/api/posthog')
          track(oem ? 'recording_killed_by_oem' : 'recording_killed_unknown', {
            manufacturer: oem?.manufacturer,
            lostMs: sinceLast,
          })
        } catch (_) {}
      }
      await storage.remove('pavemi_recording_in_progress')
    }
  } catch (e) {
    console.warn('[boot] recording post-mortem check failed', e)
  }
}

setupAppLifecycle()
setupDeepLinks()
setupStatusBar()
setupOta() // fire-and-forget — Android only, runs in background, doesn't block mount

app.mount('#app')
